/** TODO--delete this after it's no longer needed. This code attempts to work around:
    https://github.com/w8r/martinez/issues/68
    https://github.com/w8r/martinez/issues/69 */

import { compact } from 'lodash';

import { multiPolygonToPolygonArray, polygonArrayToXPolygon } from 'geometry/geojson/convert';
import { getOobInnerRingIndex, isInnerRingOob } from 'geometry/geojson/validator';
import { ringContainsAnyVertexes } from 'geometry/geojson/ring';

const _moveInnerRingToContainingPolygon = ({multiPolygonCoords, skipPolygonI, innerRing}) => {
  return multiPolygonCoords.some((polygonCoords, polygonI) => {
    if (polygonI === skipPolygonI) { return false; }
    // Add the inner ring to the polygon and check if the newly added ring is OOB.
    polygonCoords.push(innerRing);
    const success = !getOobInnerRingIndex(polygonCoords);
    if (!success) { // Revert polygon to original rings.
      multiPolygonCoords[polygonI] = polygonCoords.slice(0, -1);
    }
    return success;
  });
};

const _deleteInnerRing = ({polygonCoords, deleteRingI}) => {
  return compact(polygonCoords.map((ring, ringI) => ringI === deleteRingI ? null : ring));
};

/** If an inner ring is not contained by the outer ring of its polygon, an attempt will be found
    to move the inner ring to a different polygon where it would be valid. (Fix for #69) If that attempt fails,
    the inner ring is removed. (Fix for #68).
    @param multiPolygonCoords MultiPolygon coordinates. Will mutate individual members at polygon coordinate level,
      but not change membership of multipolygon array levels. */
export const _fixOobInnerRingsInMultiPolygonCoordinates = (multiPolygonCoords) => {
  multiPolygonCoords.forEach((polygonCoords, polygonI) => {
    let oobInnerRingI = getOobInnerRingIndex(polygonCoords);
    while (oobInnerRingI) {
      _moveInnerRingToContainingPolygon(
        {multiPolygonCoords, skipPolygonI:polygonI, innerRing:polygonCoords[oobInnerRingI]});
      multiPolygonCoords[polygonI] = _deleteInnerRing({polygonCoords, deleteRingI:oobInnerRingI});
      polygonCoords = multiPolygonCoords[polygonI];
      oobInnerRingI = getOobInnerRingIndex(polygonCoords); // Check again for more OOB rings.
    }
  });
};

export const fixOobInnerRingsInXPolygon = (xPolygon) => {
  if (xPolygon.type === 'Polygon') {
    const multiPolygonCoords = [xPolygon.coordinates];
    _fixOobInnerRingsInMultiPolygonCoordinates(multiPolygonCoords); // Mutates at 1 level deep
    xPolygon.coordinates = multiPolygonCoords[0];
  } else {
    _fixOobInnerRingsInMultiPolygonCoordinates(xPolygon.coordinates); // Mutates at 1 level deep
  }
  return xPolygon;
};

const _getPolygonCoordinatesWithAddedRing = ({toPolygon, ring}) => {
  const newCoordinates = toPolygon.coordinates.slice();
  newCoordinates.push(ring); // TODO - consider winding.
  return newCoordinates;
};

const _doInnerRingsIntersect = (polygonCoords) =>
  polygonCoords.some((leftRing, leftRingI) =>
    polygonCoords.some((rightRing, rightRingI) =>
      leftRingI !== 0 && rightRingI > leftRingI && ringContainsAnyVertexes(leftRing, rightRing)
    )
  );

/** This changes polygons to inner rings of a containing polygon, if they meet the following criteria:
    1. Polygon to change is entirely contained by another polygon.
    2. Polygon does not have inner rings.
    3. Polygon does not intersect with any inner rings of containing polygon.
    This function is coupled to results produced by a known bug in Martinez union(). Criteria #1 is
    used to find polygons that should have been interior rings. Criteria #2 and #3 are used to make
    sure some extra trouble will not be caused by this hack.

    @param multiPolygon Assume inner members will get mutated, but not a fully in-place operation.
    @return An xPolygon with changes. */
export const fixPolygonsThatShouldBeInnerRingsInMultiPolygon = (multiPolygon) => {
  const polygons = multiPolygonToPolygonArray(multiPolygon);
  polygons.forEach((movePolygon, movePolygonI) => {
    if (movePolygon.coordinates.length > 1) { return; } // fails criterion #2
    polygons.forEach((toPolygon, toPolygonI) => {
      if (toPolygon === null /* deleted polygon */ || toPolygonI === movePolygonI) { return; }
      const newCoordinates = _getPolygonCoordinatesWithAddedRing({toPolygon, ring:movePolygon.coordinates[0]});
      const addedRingI = newCoordinates.length - 1;
      if (!isInnerRingOob(newCoordinates, addedRingI) // meets criterion #1
          && !_doInnerRingsIntersect(newCoordinates)) { // meets criteria #3
        toPolygon.coordinates = newCoordinates;
        polygons[movePolygonI] = null;
      }
    });
  });
  const xPolygon = polygonArrayToXPolygon(compact(polygons));
  return xPolygon;
};
